import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import StickyQuoteBar from '../../components/feature/StickyQuoteBar';

export default function DSCRLoanBenefitsPage() {
  const benefits = [
    {
      icon: 'ri-file-forbid-line',
      title: 'No Income Verification Required',
      description: 'No W2s, tax returns, or pay stubs needed. Perfect for self-employed investors with complex tax situations.',
      details: 'DSCR loans qualify you based on property income, not personal income documentation.'
    },
    {
      icon: 'ri-infinity-line',
      title: 'Unlimited Properties',
      description: 'No restrictions on the number of financed investment properties in your portfolio.',
      details: 'Scale your real estate business without hitting conventional loan limits (typically 10 properties).'
    },
    {
      icon: 'ri-speed-up-line',
      title: 'Faster Approval Process',
      description: 'Streamlined underwriting focused on property performance, not personal finances.',
      details: 'Close in as little as 2-3 weeks compared to 45-60 days for conventional loans.'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Flexible Qualification',
      description: 'Based on rental income and DSCR ratio, not personal debt-to-income ratios.',
      details: 'Qualify even with high personal debt or multiple mortgages on your primary residence.'
    },
    {
      icon: 'ri-building-line',
      title: 'Multiple Property Types',
      description: 'Finance single-family, multifamily, short-term rentals, and commercial properties.',
      details: 'One loan program works for diverse investment strategies and property types.'
    },
    {
      icon: 'ri-user-line',
      title: 'LLC & Entity Friendly',
      description: 'Close in your LLC or business entity name for asset protection and tax benefits.',
      details: 'Maintain separation between personal and business assets while building your portfolio.'
    }
  ];

  const advantages = [
    {
      category: 'For Self-Employed Investors',
      points: [
        'No need to show two years of tax returns',
        'Write-offs don\'t hurt your qualification',
        'Complex business structures accepted',
        'Focus on property income, not personal income'
      ]
    },
    {
      category: 'For Portfolio Growth',
      points: [
        'No limit on number of properties',
        'Faster closings mean more deals',
        'Cash-out refinance without income docs',
        'Scale without conventional loan restrictions'
      ]
    },
    {
      category: 'For Experienced Investors',
      points: [
        'Leverage property performance',
        'Multiple property types accepted',
        'Entity ownership allowed',
        'Professional investor programs available'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <Navbar />
      <StickyQuoteBar />

      {/* Hero Section */}
      <section className="bg-gradient-to-r from-slate-900 via-blue-900 to-slate-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="text-emerald-400">DSCR Loan</span> Benefits
            </h1>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
              Discover why DSCR loans are the preferred financing choice for real estate investors. No income verification, unlimited properties, and fast approvals.
            </p>
            <Link
              to="/apply"
              className="inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-amber-500/30 whitespace-nowrap"
            >
              <i className="ri-file-text-line mr-2"></i>
              Start Your Application
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Key Benefits Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Top Benefits of DSCR Loans
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all border-2 border-transparent hover:border-emerald-500"
              >
                <div className={`bg-gradient-to-br from-blue-600 to-blue-700 w-16 h-16 rounded-xl flex items-center justify-center mb-6 shadow-lg ${benefit.shadow}`}>
                  <i className={`${benefit.icon} text-3xl text-white`}></i>
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">{benefit.title}</h3>
                <p className="text-slate-700 mb-3">{benefit.description}</p>
                <p className="text-sm text-slate-600 italic">{benefit.details}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages by Investor Type */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">
            Advantages of DSCR Loans by Investor Type
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            {advantages.map((advantage, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.15 }}
                className="bg-white rounded-2xl p-6 shadow-lg"
              >
                <h3 className="text-xl font-bold text-blue-900 mb-4">{advantage.category}</h3>
                <ul className="space-y-3">
                  {advantage.points.map((point, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-double-line text-blue-600 text-xl mr-2 mt-0.5"></i>
                      <span className="text-slate-700">{point}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <article className="prose prose-lg max-w-none">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Understanding the Benefits of DSCR Loans
            </h2>
            
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              DSCR loan benefits make them the financing solution of choice for serious real estate investors across the United States. Unlike traditional mortgages that focus on personal income and employment history, DSCR loans evaluate your qualification based on the property's ability to generate rental income. This fundamental difference creates numerous advantages that help investors scale their portfolios faster and more efficiently.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              The #1 Benefit: No Income Verification (No W2 Required)
            </h3>
            
            <p className="text-slate-700 mb-4">
              The most significant advantage of DSCR loans is the elimination of personal income verification. You don't need to provide W2 forms, tax returns, pay stubs, or employment verification letters. This DSCR loan benefit is particularly valuable for:
            </p>

            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-user-star-line text-blue-600 text-xl mr-3 mt-1"></i>
                  <span><strong>Self-employed investors:</strong> Your business write-offs and deductions no longer hurt your loan qualification. DSCR loans focus on property income, not your adjusted gross income.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-briefcase-line text-blue-600 text-xl mr-3 mt-1"></i>
                  <span><strong>Business owners:</strong> Complex business structures, multiple LLCs, and S-Corp distributions don't complicate your application. The property's rental income is all that matters.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-money-dollar-circle-line text-blue-600 text-xl mr-3 mt-1"></i>
                  <span><strong>High-net-worth individuals:</strong> Privacy-conscious investors appreciate not having to disclose detailed personal financial information for every property purchase.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-team-line text-blue-600 text-xl mr-3 mt-1"></i>
                  <span><strong>Retirees and passive investors:</strong> Limited personal income doesn't prevent you from expanding your rental portfolio when properties generate sufficient cash flow.</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Unlimited Properties: Scale Without Restrictions
            </h3>
            
            <p className="text-slate-700 mb-4">
              One of the most powerful DSCR loan benefits is the ability to finance unlimited investment properties. Conventional mortgages typically limit investors to 10 financed properties, creating a ceiling on portfolio growth. DSCR loans remove this restriction entirely.
            </p>

            <p className="text-slate-700 mb-6">
              Whether you're financing your 5th property or your 50th, DSCR loans evaluate each property on its own merits. This advantage allows serious investors to scale their portfolios without artificial limitations, making DSCR loans the preferred choice for building substantial real estate wealth.
            </p>

            <div className="bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-6 mb-8 border-l-4 border-blue-600">
              <h4 className="font-bold text-slate-900 mb-3">Portfolio Growth Example:</h4>
              <p className="text-slate-700 mb-3">
                An investor with 10 conventional mortgages hits the Fannie Mae limit and can't qualify for more traditional financing. With DSCR loans, they can continue acquiring properties 11, 12, 13, and beyond - each evaluated independently based on rental income.
              </p>
              <p className="text-slate-700 text-sm italic">
                This DSCR loan benefit alone has enabled countless investors to build portfolios of 20, 30, or even 100+ rental properties.
              </p>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Faster Approval and Closing Process
            </h3>
            
            <p className="text-slate-700 mb-4">
              Speed is a critical advantage in competitive real estate markets. DSCR loan benefits include significantly faster approval and closing timelines compared to conventional financing. While traditional mortgages often take 45-60 days to close, DSCR loans can close in as little as 2-3 weeks.
            </p>

            <p className="text-slate-700 mb-6">
              The streamlined underwriting process focuses on property performance rather than extensive personal financial documentation. This efficiency provides several strategic advantages:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-100">
                <h4 className="font-bold text-slate-900 mb-3 flex items-center">
                  <i className="ri-auction-line text-blue-600 text-2xl mr-2"></i>
                  Competitive Advantage
                </h4>
                <p className="text-slate-700 text-sm">
                  Faster closings make your offers more attractive to sellers. A 21-day close with DSCR financing often beats a 45-day conventional offer, even at the same price.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-100">
                <h4 className="font-bold text-slate-900 mb-3 flex items-center">
                  <i className="ri-time-line text-blue-600 text-2xl mr-2"></i>
                  More Deals Per Year
                </h4>
                <p className="text-slate-700 text-sm">
                  Closing properties in 3 weeks instead of 6 weeks means you can complete more transactions annually, accelerating your portfolio growth and wealth building.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-100">
                <h4 className="font-bold text-slate-900 mb-3 flex items-center">
                  <i className="ri-funds-line text-blue-600 text-2xl mr-2"></i>
                  Reduced Carrying Costs
                </h4>
                <p className="text-slate-700 text-sm">
                  Shorter closing periods mean less time paying for bridge loans, hard money interest, or double mortgages while waiting for refinancing to complete.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-100">
                <h4 className="font-bold text-slate-900 mb-3 flex items-center">
                  <i className="ri-calendar-check-line text-blue-600 text-2xl mr-2"></i>
                  Predictable Timeline
                </h4>
                <p className="text-slate-700 text-sm">
                  Streamlined underwriting means fewer surprises and delays. You can confidently schedule closings and plan your investment strategy with reliable timelines.
                </p>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Flexible Qualification Based on Property Performance
            </h3>
            
            <p className="text-slate-700 mb-4">
              DSCR loan benefits include qualification flexibility that conventional mortgages simply can't match. Instead of analyzing your personal debt-to-income ratio, employment history, and income stability, DSCR loans focus on one key metric: the property's debt service coverage ratio.
            </p>

            <p className="text-slate-700 mb-6">
              This approach provides significant advantages for investors in various situations:
            </p>

            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <ul className="space-y-3">
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-blue-600 text-xl mr-3 mt-1"></i>
                  <span><strong>High personal debt doesn't matter:</strong> Your car loans, credit cards, student loans, and personal mortgage don't factor into DSCR loan qualification. Only the investment property's income and expenses are considered.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-blue-600 text-xl mr-3 mt-1"></i>
                  <span><strong>Job changes are irrelevant:</strong> Switching careers, starting a business, or taking time off work doesn't impact your ability to qualify for DSCR loans on cash-flowing properties.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-blue-600 text-xl mr-3 mt-1"></i>
                  <span><strong>Multiple mortgages accepted:</strong> Having 5, 10, or 15 existing mortgages doesn't hurt your qualification. Each property is evaluated independently based on its own rental income.</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-arrow-right-circle-fill text-blue-600 text-xl mr-3 mt-1"></i>
                  <span><strong>Recent credit events:</strong> While you still need decent credit (typically 680+), recent job changes, income fluctuations, or business restructuring don't derail your application.</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              LLC and Entity Ownership Benefits
            </h3>
            
            <p className="text-slate-700 mb-4">
              A unique advantage of DSCR loans is the ability to close in your LLC or business entity name. This DSCR loan benefit provides crucial asset protection and tax planning advantages that conventional mortgages typically don't allow.
            </p>

            <p className="text-slate-700 mb-6">
              When you hold investment properties in an LLC, you create legal separation between your personal assets and your rental properties. This structure protects your personal wealth from potential lawsuits or liability issues related to your rental properties. DSCR loans make this professional approach accessible without requiring personal guarantees or complex workarounds.
            </p>

            <div className="bg-blue-50 rounded-xl p-6 mb-8">
              <h4 className="font-bold text-slate-900 mb-3">Entity Ownership Advantages:</h4>
              <ul className="space-y-2 text-slate-700">
                <li className="flex items-start">
                  <i className="ri-shield-check-line text-blue-600 mr-2 mt-1"></i>
                  <span><strong>Asset Protection:</strong> Separate legal entities protect personal assets from rental property liabilities</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-file-list-3-line text-blue-600 mr-2 mt-1"></i>
                  <span><strong>Tax Benefits:</strong> Business entity structures may offer tax advantages and deduction opportunities</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-organization-chart text-blue-600 mr-2 mt-1"></i>
                  <span><strong>Professional Structure:</strong> Demonstrates sophisticated investor approach to lenders and partners</span>
                </li>
                <li className="flex items-start">
                  <i className="ri-exchange-line text-blue-600 mr-2 mt-1"></i>
                  <span><strong>Estate Planning:</strong> Easier to transfer ownership or bring in partners through entity structures</span>
                </li>
              </ul>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Multiple Property Types Accepted
            </h3>
            
            <p className="text-slate-700 mb-4">
              DSCR loan benefits extend to the variety of property types you can finance. Unlike conventional mortgages with strict property type restrictions, DSCR loans accommodate diverse investment strategies:
            </p>

            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-200">
                <h4 className="font-bold text-slate-900 mb-2 text-lg">Traditional Rentals</h4>
                <p className="text-slate-700 text-sm">Single-family homes, condos, townhouses with long-term tenants</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-200">
                <h4 className="font-bold text-slate-900 mb-2 text-lg">Short-Term Rentals</h4>
                <p className="text-slate-700 text-sm">Airbnb, VRBO, vacation rentals with nightly/weekly bookings</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-200">
                <h4 className="font-bold text-slate-900 mb-2 text-lg">Multifamily Properties</h4>
                <p className="text-slate-700 text-sm">Duplexes, triplexes, fourplexes, and 5+ unit apartment buildings</p>
              </div>
              <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-5 border border-blue-200">
                <h4 className="font-bold text-slate-900 mb-2 text-lg">Mixed-Use Properties</h4>
                <p className="text-slate-700 text-sm">Properties combining residential and commercial spaces</p>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              DSCR Loans vs Traditional Mortgages: Pros and Cons
            </h3>
            
            <p className="text-slate-700 mb-4">
              While DSCR loan benefits are substantial, it's important to understand both the advantages and potential drawbacks compared to conventional financing:
            </p>

            <div className="overflow-x-auto mb-8">
              <table className="w-full bg-white rounded-xl shadow-lg overflow-hidden">
                <thead className="bg-blue-900 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Feature</th>
                    <th className="px-6 py-4 text-left">DSCR Loans</th>
                    <th className="px-6 py-4 text-left">Conventional Loans</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  <tr>
                    <td className="px-6 py-4 font-semibold">Income Verification</td>
                    <td className="px-6 py-4 text-green-600">✓ Not Required</td>
                    <td className="px-6 py-4 text-red-600">✗ Required</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-semibold">Property Limit</td>
                    <td className="px-6 py-4 text-green-600">✓ Unlimited</td>
                    <td className="px-6 py-4 text-red-600">✗ 10 Properties Max</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-semibold">Closing Speed</td>
                    <td className="px-6 py-4 text-green-600">✓ 2-3 Weeks</td>
                    <td className="px-6 py-4 text-yellow-600">~ 45-60 Days</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-semibold">LLC Ownership</td>
                    <td className="px-6 py-4 text-green-600">✓ Allowed</td>
                    <td className="px-6 py-4 text-red-600">✗ Usually Not Allowed</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-semibold">Interest Rates</td>
                    <td className="px-6 py-4 text-yellow-600">~ 6.99% - 9.50%</td>
                    <td className="px-6 py-4 text-green-600">✓ Slightly Lower</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-semibold">Down Payment</td>
                    <td className="px-6 py-4 text-yellow-600">~ 20-25%</td>
                    <td className="px-6 py-4 text-green-600">✓ 15-25%</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Who Benefits Most from DSCR Loans?
            </h3>
            
            <p className="text-slate-700 mb-4">
              While DSCR loan benefits apply to all real estate investors, certain investor profiles gain the most significant advantages:
            </p>

            <div className="space-y-4 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-600">
                <h4 className="font-bold text-slate-900 mb-2">Self-Employed & Business Owners</h4>
                <p className="text-slate-700">
                  If you write off business expenses, have multiple income streams, or operate through S-Corps or partnerships, DSCR loans eliminate the documentation headaches that make conventional financing difficult.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-600">
                <h4 className="font-bold text-slate-900 mb-2">Portfolio Investors (10+ Properties)</h4>
                <p className="text-slate-700">
                  Once you hit the conventional loan limit, DSCR loans become essential for continued growth. The unlimited property benefit makes them indispensable for serious portfolio building.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-600">
                <h4 className="font-bold text-slate-900 mb-2">Short-Term Rental Investors</h4>
                <p className="text-slate-700">
                  Airbnb and vacation rental investors benefit from DSCR loans that recognize higher short-term rental income, often qualifying for larger loan amounts than conventional financing would allow.
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-blue-600">
                <h4 className="font-bold text-slate-900 mb-2">Privacy-Conscious Investors</h4>
                <p className="text-slate-700">
                  High-net-worth individuals who prefer not to disclose detailed personal financial information appreciate the privacy benefits of DSCR loans focused solely on property performance.
                </p>
              </div>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mt-8 mb-4">
              Frequently Asked Questions About DSCR Loan Benefits
            </h3>

            <div className="space-y-6 mb-8">
              <div className="bg-slate-50 rounded-xl p-6">
                <h4 className="font-bold text-slate-900 mb-2">What are the main benefits of DSCR loans?</h4>
                <p className="text-slate-700">
                  The primary DSCR loan benefits include no income verification (no W2 or tax returns), unlimited properties, faster approval and closing, flexible qualification based on property income, LLC ownership allowed, and acceptance of multiple property types including short-term rentals.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <h4 className="font-bold text-slate-900 mb-2">Are DSCR loans better than conventional mortgages?</h4>
                <p className="text-slate-700">
                  DSCR loans offer significant advantages for real estate investors, particularly self-employed individuals and those with multiple properties. While interest rates may be slightly higher, the benefits of no income verification, unlimited properties, and faster closings often outweigh the modest rate difference.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <h4 className="font-bold text-slate-900 mb-2">What are the advantages of DSCR loans for self-employed investors?</h4>
                <p className="text-slate-700">
                  Self-employed investors benefit tremendously from DSCR loans because business write-offs and deductions don't hurt qualification. You don't need to provide tax returns, and complex business structures are accepted. The focus is entirely on the property's rental income, not your personal income documentation.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <h4 className="font-bold text-slate-900 mb-2">Can I really finance unlimited properties with DSCR loans?</h4>
                <p className="text-slate-700">
                  Yes! Unlike conventional mortgages that limit investors to 10 financed properties, DSCR loans have no property count restrictions. Each property is evaluated independently based on its own rental income and DSCR ratio, allowing unlimited portfolio growth.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <h4 className="font-bold text-slate-900 mb-2">What are the pros and cons of DSCR loans?</h4>
                <p className="text-slate-700">
                  Pros include no income verification, unlimited properties, faster closings, LLC ownership, and flexible qualification. Cons include slightly higher interest rates (typically 0.5-1.5% more than conventional) and minimum down payments of 20-25%. For most investors, the benefits far outweigh the drawbacks.
                </p>
              </div>

              <div className="bg-slate-50 rounded-xl p-6">
                <h4 className="font-bold text-slate-900 mb-2">Do DSCR loans work for short-term rentals?</h4>
                <p className="text-slate-700">
                  Absolutely! One of the key advantages of DSCR loans is their acceptance of short-term rental income. Lenders use projected Airbnb/VRBO income based on market data, often resulting in higher qualification amounts than traditional financing would allow for the same property.
                </p>
              </div>
            </div>

            <div className="bg-gradient-to-r from-blue-900 to-blue-800 text-white rounded-2xl p-8 text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Experience the Benefits of DSCR Loans</h3>
              <p className="text-blue-100 mb-6 max-w-2xl mx-auto">
                Stop letting income documentation and property limits hold back your real estate investing. Get connected with top DSCR lenders and start leveraging these powerful benefits today.
              </p>
              <Link
                to="/apply"
                className="inline-flex items-center justify-center bg-gradient-to-r from-amber-500 to-amber-600 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-amber-600 hover:to-amber-700 transition-all shadow-lg hover:shadow-amber-500/30 whitespace-nowrap"
              >
                <i className="ri-file-text-line mr-2"></i>
                Apply for DSCR Loan
              </Link>
              <a
                href="tel:8005557327"
                className="inline-flex items-center justify-center bg-white/10 backdrop-blur-sm text-white border-2 border-white/30 px-8 py-4 rounded-xl font-bold text-lg hover:bg-white/20 hover:border-emerald-400 transition-all whitespace-nowrap"
              >
                <i className="ri-phone-line mr-2"></i>
                Call (800) 555-DSCR
              </a>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}